import React from 'react';
import { Box, Card, Divider, Flex, Title } from '@mantine/core';
import { TextComponent } from '@manifest-cyber/design-system/lib/components/TextComponent';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/Icon/Icon';
import styles from '../ModelDetail.module.scss';
import { GetAiModelDetailResponseData } from '@manifest-cyber/types/interface/contracts/model-analysis/get.contract';
import { IconName } from '@fortawesome/fontawesome-svg-core';

interface RiskOverviewCardProps {
  modelData: GetAiModelDetailResponseData;
}

export const RiskOverviewCard: React.FC<RiskOverviewCardProps> = ({ modelData }) => {
  const { t } = useTranslation();

  const getRiskColor = (riskScore: string | undefined) => {
    if (riskScore === 'high') return 'red';
    if (riskScore === 'medium') return 'yellow';
    if (riskScore === 'low') return 'green';
    return 'muted';
  };

  const getFindingIcon = (category: string | undefined) => {
    if (category === 'violation')
      return { icon: 'ban' as IconName, className: styles.ban };
    if (category === 'needs_review')
      return { icon: 'triangle-exclamation' as IconName, className: styles.warning };
    return { icon: 'circle-check' as IconName, className: styles.check };
  };

  return (
    <Card shadow="sm" padding="lg" withBorder className={styles.cardContainer}>
      <Title order={3} mb="md">
        {t('mosaic.modelDetail.cards.riskOverview.title')}
      </Title>
      <Flex w="100%" gap="md" align="center">
        <Box w="50%">
          <Flex justify="center">
            <TextComponent
              variant="displayXLarge"
              transform="capitalize"
              color={getRiskColor(modelData.riskOverview?.riskScore)}
            >
              {modelData.riskOverview?.riskScore || t('global.none')}
            </TextComponent>
          </Flex>
        </Box>
        <Box w="50%" mr={30}>
          <Flex gap={20} direction="column">
            <ComplianceStatusItem
              label={t(
                'mosaic.modelDetail.cards.riskOverview.complianceCategories.violations',
              )}
              count={modelData.riskOverview?.countComplianceStatuses?.violations}
              iconName={'ban' as IconName}
              iconClass={styles.ban}
            />
            <ComplianceStatusItem
              label={t(
                'mosaic.modelDetail.cards.riskOverview.complianceCategories.needsReview',
              )}
              count={modelData.riskOverview?.countComplianceStatuses?.needsReview}
              iconName={'triangle-exclamation' as IconName}
              iconClass={styles.warning}
            />
            <ComplianceStatusItem
              label={t(
                'mosaic.modelDetail.cards.riskOverview.complianceCategories.inCompliance',
              )}
              count={modelData.riskOverview?.countComplianceStatuses?.inCompliance}
              iconName={'circle-check' as IconName}
              iconClass={styles.check}
            />
          </Flex>
        </Box>
      </Flex>
      <Divider mt={40} mb={10} />
      <TextComponent variant="headerAllCaps" transform="uppercase" color="muted">
        {t('mosaic.modelDetail.cards.riskOverview.findings')}
      </TextComponent>
      <div className={styles.findingsContainer}>
        {modelData.riskOverview?.findings?.map((finding, index) => {
          const { icon, className } = getFindingIcon(finding.category);
          return (
            <Flex direction="row" gap={8} mt={12} key={index}>
              <Icon icon={icon} className={className} />
              <TextComponent variant="componentSmall" className={styles.findings}>
                {finding.description}
              </TextComponent>
            </Flex>
          );
        })}
      </div>
    </Card>
  );
};

interface ComplianceStatusItemProps {
  label: string;
  count?: number;
  iconName: IconName;
  iconClass?: string;
}

const ComplianceStatusItem: React.FC<ComplianceStatusItemProps> = ({
  label,
  count,
  iconName,
  iconClass = '',
}) => {
  return (
    <Flex direction="row" justify="space-between" align="center" gap={8}>
      <TextComponent variant="headerAllCaps" transform="uppercase" color="muted">
        {label}
      </TextComponent>
      <Flex direction="row" gap={8} align="center">
        <div className={styles.complianceIconContainer}>
          <Icon icon={iconName} className={iconClass} />
        </div>
        <TextComponent
          variant="componentSmall"
          className={styles.complianceCount}
          color={count ? 'white' : 'muted'}
        >
          {count || '--'}
        </TextComponent>
      </Flex>
    </Flex>
  );
};
